import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import CommonInput from "../../../components/input/CommonInput";
import PhoneInput from "../../../components/input/PhoneInput";
import CommonButton from "../../../components/button/CommonButton";
import CountryModal from "../../../components/modal/CountryModal";
import { appApiHandler } from "../../../api/appApiHandler";
import { showErrorToast, showSuccessToast } from "../../../utils/toast";
import FullscreenLoader from "../../../components/loader/fullScreenLoader/FullscreenLoader";
import "./AddAddress.css";
// import HomeHeader from "../../../components/header/HomeHeader";
import CommonFooter from "../../../components/footer/CommonFooter";
import { getErrorMessage } from "../../../constants/globalFunction";
import { string } from "../../../constants/string";
import Breadcrumb from "../../../components/breadcrumb";

const AddAddress = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { userData } = useSelector((state) => state.userState);

  const { item = null } = location?.state || {};
  const isEditMode = !!item;

  const [name, setName] = useState("");
  const [phoneno, setPhoneno] = useState("");
  const [countryCode, setCountryCode] = useState("+91");
  const [address, setAddress] = useState("");
  const [country, setCountry] = useState(null);
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [pincode, setPincode] = useState("");

  const [nameError, setNameError] = useState("");
  const [addressError, setAddressError] = useState("");
  const [phonenoError, setPhonenoError] = useState("");
  const [countryError, setCountryError] = useState("");
  const [cityError, setCityError] = useState("");
  const [stateError, setStateError] = useState("");
  const [pincodeError, setPincodeError] = useState("");

  const [loading, setLoading] = useState(false);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [countryModalVisible, setCountryModalVisible] = useState(false);
  const [countryList, setCountryList] = useState([]);

  const getCountryList = async () => {
    const response = await appApiHandler.getCountries();
    if (response?.status === 200) {
      setCountryList(response?.data);
    }
  };

  useEffect(() => {
    setLoading(true);
    Promise.all([getCountryList()]).then(() => {
      setLoading(false);
    });

    if (isEditMode && item) {
      setName(item.name || "");
      setPhoneno(item.contact || "");
      setCountryCode(item.country_code || "+91");
      setAddress(item.address || "");
      setCountry(item.country || null);
      setCity(item.city || "");
      setState(item.state || "");
      setPincode(item.pincode || "");
    }
  }, [isEditMode, item]);

  const onSelectCountry = (selectedCountry) => {
    setCountry(selectedCountry);
    setCountryModalVisible(false);
  };

  const onPressCountry = () => {
    setCountryModalVisible(true);
  };

  const onCloseCountryModal = () => {
    setCountryModalVisible(false);
  };

  const validateValues = () => {
    let isValid = true;

    if (!userData) {
      showErrorToast(string.error.please_login_to_your_account);
      return;
    }

    if (!name.trim()) {
      setNameError("Please enter name");
      isValid = false;
    } else {
      setNameError("");
    }

    if (!phoneno.trim()) {
      setPhonenoError("Please enter contact number");
      isValid = false;
    } else {
      setPhonenoError("");
    }

    if (countryCode === "+91") {
      const digitsOnlyLength = phoneno.trim().length;
      if (digitsOnlyLength !== 10) {
        setPhonenoError("Please enter valid phone number");
        isValid = false;
      }
    }

    if (!address.trim()) {
      setAddressError("Please enter address");
      isValid = false;
    } else {
      setAddressError("");
    }

    if (!country) {
      setCountryError("Please select country");
      isValid = false;
    } else {
      setCountryError("");
    }

    if (!pincode.trim()) {
      setPincodeError("Please enter pincode");
      isValid = false;
    } else {
      setPincodeError("");
    }

    if (!city.trim()) {
      setCityError("Please enter city");
      isValid = false;
    } else {
      setCityError("");
    }

    if (!state.trim()) {
      setStateError("Please enter state");
      isValid = false;
    } else {
      setStateError("");
    }

    return isValid;
  };

  const onSubmit = async () => {
    const isValid = validateValues();
    if (isValid) {
      const reqData = {
        user_id: userData?._id,
        name: name.trim(),
        contact: phoneno.trim(),
        country_code: countryCode,
        address: address.trim(),
        country_id: country?._id,
        pincode: pincode.trim(),
        state: state.trim(),
        city: city.trim(),
      };

      if (isEditMode && item) {
        reqData.address_id = item._id;
      }

      setButtonLoading(true);
      const response = isEditMode
        ? await appApiHandler.updateAddress(reqData)
        : await appApiHandler.addAddress(reqData);

      setButtonLoading(false);
      if (response?.status === 200) {
        showSuccessToast(
          isEditMode
            ? "Address updated successfully"
            : "Address added successfully"
        );
        navigate(-1);
      } else if (response?.message) {
        showErrorToast(getErrorMessage(response?.message));
      } else {
        showErrorToast(string.error.failed_due_to_internal_error);
      }
    }
  };

  const onChangeCountryCode = (country) => {
    setCountryCode(country.dial_code);
  };

  return (
    <div className="add-address-page">
      {/* <HomeHeader /> */}
      <Breadcrumb
        items={[
          {
            label: "Home",
            path: "/",
          },
          {
            label: "Address",
            path: "/address",
          },
          {
            label: isEditMode ? "Edit Address" : "Add New Address",
            isActive: true,
          },
        ]}
      />
      {loading ? (
        <div className="add-address-loading">
          <FullscreenLoader text="Loading..." />
        </div>
      ) : (
        <div className="add-address-container">
          <div className="add-address-form">
            <CommonInput
              label="Name"
              value={name}
              onChange={setName}
              error={nameError}
              editable={!buttonLoading}
              placeholder="Enter name"
            />

            <PhoneInput
              label="Contact"
              code={countryCode}
              onChangeCode={onChangeCountryCode}
              onChangeText={setPhoneno}
              value={phoneno}
              editable={!buttonLoading}
              placeholder="Enter phone number"
              error={phonenoError}
            />

            <CommonInput
              label="Address"
              value={address}
              onChange={setAddress}
              error={addressError}
              editable={!buttonLoading}
              placeholder="Enter address"
              multiline={true}
            />

            <div className="country-container">
              <label className="country-label">
                Country<span className="required-text">*</span>
              </label>
              <button
                className={`country-input-field ${countryError ? "error" : ""
                  } ${buttonLoading ? "disabled" : ""}`}
                onClick={onPressCountry}
                disabled={buttonLoading}
                type="button"
              >
                <span className={`country-text ${country ? "selected" : ""}`}>
                  {country ? country?.name : "Select country"}
                </span>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M6 9L12 15L18 9"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              {countryError && (
                <div className="country-error">{countryError}</div>
              )}
            </div>

            <CommonInput
              label="State"
              value={state}
              onChange={setState}
              error={stateError}
              placeholder="Enter state"
            />

            <CommonInput
              label="City"
              value={city}
              onChange={setCity}
              error={cityError}
              placeholder="Enter city"
            />

            <CommonInput
              label="Pincode"
              value={pincode}
              onChange={setPincode}
              error={pincodeError}
              editable={!buttonLoading}
              placeholder="Enter pincode"
            />
          </div>

          <CommonButton
            title={buttonLoading ? "Saving..." : "Save"}
            onPress={onSubmit}
            disabled={buttonLoading}
            customClassName="save-address-button"
          />
        </div>
      )}

      <CountryModal
        visible={countryModalVisible}
        onClose={onCloseCountryModal}
        onSelectCountry={onSelectCountry}
        selectedCountry={country}
        countryList={countryList}
      />
      <CommonFooter />
    </div>
  );
};

export default AddAddress;
